import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle } from "lucide-react";
import { ErrorMessage } from "../components/ErrorMessage";
import { COMPLEXITY_RULES, firstUnmetRule } from "../lib/passwordPolicy";
import { verifySignup, SignupApiError } from "../lib/signupApi";

// Public self-signup verification (Phase 2). The user arrives from the
// emailed link, which carries ?email= and ?code=. They set a password to
// activate the account. On success the account is ACTIVE, or
// PENDING_APPROVAL when the org gates new members; we surface the
// server's message so the user learns whether approval is still pending.

export function VerifySignup() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email")?.trim() ?? "";
  const code = searchParams.get("code")?.trim() ?? "";
  const linkValid = email.length > 0 && code.length > 0;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Holds the server's success message once verification completes;
  // non-null flips the page to the success state. Carries the
  // pending-approval wording when the org requires approval.
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const localError = password ? firstUnmetRule(password) : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (localError) {
      setError(localError);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const result = await verifySignup({ email, code, password });
      // Render the server message verbatim; it distinguishes an active
      // account from one that still needs approval.
      setSuccessMessage(result.message);
    } catch (err: unknown) {
      // 400 covers bad/expired/used codes AND password-policy failures,
      // both of which are safe to show verbatim; 429 is rate limiting.
      if (err instanceof SignupApiError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Something went wrong. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg p-4">
      <div className="auth-shell">
        <div className="auth-panel">
          <div className="auth-panel-header px-8 py-6 text-center">
            <h1 className="brand-logo text-2xl">Payment Products</h1>
            <p className="mt-1 text-sm text-brand-muted">Verify your account</p>
          </div>

          {!linkValid ? (
            <div className="space-y-4 p-8">
              <ErrorMessage message="This verification link is missing information. Open the most recent link from your email, or request a new one from the sign-up page." />
              <p className="text-center text-sm text-brand-muted">
                <Link to="/signup" className="link">
                  Back to sign up
                </Link>
              </p>
            </div>
          ) : successMessage ? (
            <div className="space-y-4 p-8 text-center" role="status">
              <CheckCircle2
                className="mx-auto h-10 w-10 text-emerald-300"
                aria-hidden="true"
              />
              <h2 className="text-lg font-semibold text-brand-text">
                You're all set
              </h2>
              <p className="text-sm text-brand-muted">{successMessage}</p>
              <Link to="/signin" className="btn-primary inline-block px-6 py-2.5">
                Go to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 p-8">
              {error && <ErrorMessage message={error} />}

              <p className="text-sm text-brand-muted">
                Setting the password for{" "}
                <span className="font-medium text-brand-text">{email}</span>.
              </p>

              {/* Hidden username field so password managers associate the
                  saved credential with the right account. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={email}
                readOnly
                hidden
              />

              <div>
                <label htmlFor="verify-password" className="label mb-1 block">
                  New password
                </label>
                <input
                  id="verify-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  required
                  autoFocus
                  maxLength={128}
                  aria-describedby="verify-rules"
                />
                <ul
                  id="verify-rules"
                  aria-live="polite"
                  className="mt-2 space-y-1 text-xs"
                >
                  {COMPLEXITY_RULES.map((rule) => {
                    const met = rule.test(password);
                    return (
                      <li
                        key={rule.label}
                        className={`flex items-center gap-1.5 ${
                          met ? "text-emerald-300" : "text-brand-muted"
                        }`}
                      >
                        {met ? (
                          <CheckCircle2
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        ) : (
                          <Circle className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        <span>{rule.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div>
                <label htmlFor="verify-confirm" className="label mb-1 block">
                  Confirm new password
                </label>
                <input
                  id="verify-confirm"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input"
                  required
                  maxLength={128}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-2.5"
              >
                {loading ? "Verifying..." : "Activate account"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
